import { APITypesV1 } from "@cur8/api-client";
import { TubeDrawDecisionError } from "hooks/mutations/useConfirmDrawMutation";
import { sampleTypeLabels } from "lib/tubeStatus";

function reasonFor(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// Named by type and sample ID, the two things the tube's own card shows, so the
// operator can find the tube the message is about.
function tubeName(
  bloodSampleId: string,
  tubes: readonly APITypesV1.BloodSample[]
): string {
  const tube = tubes.find(
    (candidate) => candidate.bloodSampleId === bloodSampleId
  );
  return tube
    ? `${sampleTypeLabels[tube.sampleType]} (${bloodSampleId})`
    : bloodSampleId;
}

/**
 * Names the tubes that didn't land. With one call per tube, some of a batch can
 * land and the rest fail, and "the draw confirmation failed" would leave the
 * operator re-reading every card to work out which.
 */
export function drawSubmitErrorMessage(
  error: unknown,
  tubes: readonly APITypesV1.BloodSample[]
): string {
  if (error instanceof TubeDrawDecisionError) {
    // Tubes that failed the same way are named together under that one reason,
    // and a tube that failed differently carries its own, so a second tube's
    // error is never reported as the first one's.
    const namesByReason = new Map<string, string[]>();
    for (const { bloodSampleId, error: cause } of error.failures) {
      const reason = reasonFor(cause);
      const names = namesByReason.get(reason) ?? [];
      names.push(tubeName(bloodSampleId, tubes));
      namesByReason.set(reason, names);
    }
    const parts = Array.from(
      namesByReason,
      ([reason, names]) => `${names.join(", ")}: ${reason}`
    );
    return `Failed to record ${parts.join("; ")}`;
  }
  return `Failed to record draw confirmation: ${reasonFor(error)}`;
}
